import React, { useCallback, useContext } from 'react';
import { LinkButton, Typography } from '@vtblife/uikit';

import classname from '@search/classname/src';
import { formatRange } from '@search/helpers/src/formatNumber';
import {
    Developer,
    DeveloperCard,
    NewBuildingSnippet as GQLNewBuildingSnippet,
    NewBuildingWhitelabelEnum
} from '@search/graphql-typings';
import { DATA_GTM } from '@search/vtbeco-frontend-core/domain/newbuilding/libs/constants';
import { IUseToggleValueReturnType } from '@search/hooks/src/useToggleValue';
import SnippetPhone from '@search/vtbeco-frontend-core/domain/newbuilding/components/common/SnippetPhone';
import Image from '@search/vtbeco-frontend-core/view/common/components/Image';
import { CallbackModal } from '@search/vtbeco-frontend-core/view/common/components/CallbackModal';
import { dataLayerPush } from '@search/vtbeco-frontend-core/domain/google/dataLayerPush';
import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';
import { isM2ProUser } from '@search/auth/src/helpers/isM2ProUser';

import { SvgSpriteIcon } from '../../../common/components/SvgSpriteIcon';
import { GlobalContext, IOnChangeGlobalContext, OnChangeGlobalContext } from '../../../common/components/GlobalContext';
import ChatButton from '../../../common/components/ChatButton';
import FavoritesButton from '../../../common/components/FavoritesButton';
import { useFavoriteNewBuildingButton } from '../../../common/components/FavoritesButton/useFavoriteNewBuildingButton';
import { defineCallbackEmailSender } from '../../../common/libs/defineEmailSender';
import { useDeveloperNames } from '../../../common/components/NewBuildingCard';
import { ChatStateProvider } from '../../../common/components/NewBuildingCard/ChatStateProvider';
import RewardAmountIcon from '../../../common/components/NewBuildingCard/NewBuildingCardOfferSearch/RewardAmountIcon';
import { M2ProOrderButton } from '../../../common/components/M2ProOrderButton';
import { M2ProAttachedFilesButton } from '../../../common/components/M2ProAttachedFilesButton';
import { useM2ProFiles } from '../../../common/hooks/useM2ProFiles';

import './RightBlock.css';

const cn = classname.bind(null, 'NewBuildingSerpSnippetRightBlock');

interface IRightBlock {
    newBuilding: GQLNewBuildingSnippet;
    phoneHoverToggle: IUseToggleValueReturnType;
    hoverToggle: IUseToggleValueReturnType;
    developer?: Developer | null;
    handlePhoneEventPush: () => void;
    clearSerpCache?: (refresh?: boolean) => void;
}

// eslint-disable-next-line complexity
const RightBlock: React.FunctionComponent<IRightBlock> = ({
    newBuilding,
    phoneHoverToggle,
    hoverToggle,
    developer,
    handlePhoneEventPush,
    clearSerpCache
}) => {
    const {
        instanceConfig: {
            searchIndex,
            hasFavoritesButton,
            calltouchUnitId
        },
        calltouchModId
    } = useContext(GlobalContext);

    const isVtb = searchIndex === NewBuildingWhitelabelEnum.Vtb;

    const { auth } = useM2AuthContext();

    const rewardAmount = null;
    const { prepayment, cooperationTerms, nbPresentation } = newBuilding.m2Pro ?? {};
    const hasRoleM2Pro = isM2ProUser(auth.user?.currentRoleType);
    const hasM2Pro = hasRoleM2Pro && Boolean(rewardAmount);

    const m2ProFiles = useM2ProFiles({ cooperationTerms, nbPresentation });

    const onChangeGlobalContext = useContext<IOnChangeGlobalContext>(OnChangeGlobalContext);
    const developerLink = developer && onChangeGlobalContext.linkBuilder.developer &&
        onChangeGlobalContext.linkBuilder.developer({
            id: developer.id,
            name: developer.translitTitle
        });

    const favoritesButtonProps = useFavoriteNewBuildingButton({
        newBuildingId: newBuilding.id,
        isFavorite: newBuilding.isFavorite,
        onSuccess: clearSerpCache
    });

    const developerNames = useDeveloperNames(newBuilding.developerCards as DeveloperCard[]);

    const { ranking } = newBuilding;
    const onStatEventPush = useCallback(() => dataLayerPush({
        event: 'nb_call_like',
        nb_price: newBuilding.priceMin ?? 0,
        nb_class: ranking ?? ''
    }), [ newBuilding.priceMin, ranking ]);

    const renderCallBack = useCallback((
        dataGTM?: DATA_GTM,
        sendDataGTM?: DATA_GTM,
        extra?: string,
        subjectExtra?: string
    ) => (
        <CallbackModal
            btnWrapperClass={cn('callBack')}
            newBuildingName={newBuilding.title.nominative}
            address={newBuilding.address}
            emailSender={defineCallbackEmailSender(newBuilding.isMskMO, newBuilding.isSPbLO)}
            developerName={developerNames}
            extra={extra}
            subjectExtra={subjectExtra}
            gtmSendClick={sendDataGTM}
            onSendEventPush={onStatEventPush}
            gtmShowModal={dataGTM}
            callbackUnitId={calltouchUnitId[newBuilding.isSPbLO ? 'Spb' : 'Msk']}
            withoutIconOnButton
            buttonSize='s'
        />
    ), [ newBuilding.title.nominative, newBuilding.address, newBuilding.isMskMO, newBuilding.isSPbLO, developerNames, onStatEventPush, calltouchUnitId ]);

    const renderButtons = useCallback(() => {
        if (hasM2Pro) {
            return (
                <div className={cn('m2ProButtons')}>
                    <div
                        onMouseOver={phoneHoverToggle.handleSetTrueTogVal}
                        onMouseLeave={phoneHoverToggle.handleSetFalseTogVal}
                    >
                        <M2ProOrderButton
                            newBuildingId={newBuilding.id}
                            fullWidth
                            size='s'
                            variant={
                                hoverToggle.isToggleValue || phoneHoverToggle.isToggleValue ?
                                    'primary' :
                                    'secondary'
                            }
                            eventData={{ page_type: 'serp' }}
                        />
                    </div>
                    <ChatStateProvider initialClientMessage={`Интересует ${newBuilding.title.accusative}`}>
                        <ChatButton
                            hasText
                            fullWidth
                            hasIcon={false}
                        />
                    </ChatStateProvider>
                </div>
            );
        }

        return (
            <>
                {developer && (
                    <SnippetPhone
                        phoneParams={{
                            phoneNum: 0,
                            developerId: developer.id,
                            newBuildingId: Number(newBuilding.id),
                            whitelabel: searchIndex
                        }}
                        onPhoneEventPush={handlePhoneEventPush}
                        render={({ phone, handleClick, href, loading }) => (
                            <div
                                className={cn('phone')}
                                onMouseOver={phoneHoverToggle.handleSetTrueTogVal}
                                onMouseLeave={phoneHoverToggle.handleSetFalseTogVal}
                            >
                                <LinkButton
                                    size='s'
                                    onClick={() => handleClick()}
                                    href={href}
                                    variant={
                                        hoverToggle.isToggleValue || phoneHoverToggle.isToggleValue ?
                                            'primary' :
                                            'secondary'
                                    }
                                    loading={loading}
                                >
                                    {phone}
                                </LinkButton>
                            </div>
                        )}
                        calltouchId={calltouchModId}
                    />
                )}
                {! isVtb && (
                    <div className={cn('callbackAndChat')}>
                        <div className={cn('callback', { hasChat: newBuilding.hasChat })}>
                            {(! newBuilding.isSpecProjectRegion || newBuilding.isMskMO || newBuilding.isSPbLO) && renderCallBack(
                                DATA_GTM.CARD_CALL_BACK_BUILDING,
                                DATA_GTM.CARD_CALL_BACK_SEND_BUILDING
                            )}
                        </div>
                        {newBuilding.hasChat ? (
                            <ChatStateProvider initialClientMessage={`Интересует ${newBuilding.title.accusative}`}>
                                <ChatButton className={cn('openChat')} />
                            </ChatStateProvider>
                        ) : null}
                    </div>
                )}
            </>
        );
    }, [
        hasM2Pro,
        developer,
        newBuilding.id,
        newBuilding.hasChat,
        newBuilding.isSpecProjectRegion,
        newBuilding.title.accusative,
        newBuilding.isMskMO,
        newBuilding.isSPbLO,
        searchIndex,
        handlePhoneEventPush,
        calltouchModId,
        isVtb,
        renderCallBack,
        phoneHoverToggle.handleSetTrueTogVal,
        phoneHoverToggle.handleSetFalseTogVal,
        phoneHoverToggle.isToggleValue,
        hoverToggle.isToggleValue
    ]);

    return (
        <div className={cn()}>
            <div className={cn('content', { vtb: isVtb })}>
                <div className={cn('contentBlock')}>
                    {newBuilding.priceMin && (
                        <Typography variant='h3'>
                            {formatRange(newBuilding.priceMin, null, {
                                billionsAsString: true
                            })} ₽
                        </Typography>
                    )}
                    {hasM2Pro && (
                        <div className={cn('m2Pro', { noPrice: ! newBuilding.priceMin })}>
                            {rewardAmount && (
                                <RewardAmountIcon amount={rewardAmount} />
                            )}
                            {prepayment && (
                                <SvgSpriteIcon
                                    name='M2ProPrepayment'
                                    width={24}
                                    height={24}
                                    className={cn('m2ProPrepayment')}
                                />
                            )}
                        </div>
                    )}
                    {hasM2Pro && m2ProFiles.length > 0 && (
                        <div className={cn('m2ProAttachedFilesButton')}>
                            <M2ProAttachedFilesButton
                                view='text'
                                files={m2ProFiles}
                                eventData={{ page_type: 'serp' }}
                            />
                        </div>
                    )}
                    {
                        ! isVtb && ! hasM2Pro && newBuilding.hasEscrow && (
                            <div className={cn('escrow', { noPrice: ! newBuilding.priceMin })}>
                                <SvgSpriteIcon
                                    name='TickNew'
                                    width={16}
                                    height={16}
                                />
                                <Typography
                                    tag='span'
                                    className={cn('escrowText')}
                                    variant='primary-alone'
                                    color='black-400'
                                >
                                    Эскроу счет
                                </Typography>
                            </div>
                        )
                    }
                    {
                        isVtb && newBuilding.hasEscrow && (
                            <Typography
                                tag='span'
                                variant='primary-alone'
                            >
                                Эскроу счет
                            </Typography>
                        )
                    }
                </div>
                <div className={cn('contentBlock')}>
                    {developer && developerLink && (
                        <a
                            target='_blank'
                            className={cn('developer')}
                            href={developerLink}
                            onMouseOver={phoneHoverToggle.handleSetTrueTogVal}
                            onMouseLeave={phoneHoverToggle.handleSetFalseTogVal}
                        >
                            <Typography
                                tag='div'
                                className={cn('developerName')}
                                variant='primary-alone'
                                color='primary'
                            >
                                {developer.title}
                            </Typography>
                            <div className={cn('developerIcon')}>
                                {
                                    developer.icon?.origin ? (
                                        <Image
                                            type='contain'
                                            width={40}
                                            height={40}
                                            url={developer.icon.origin}
                                        />
                                    ) : (
                                        <SvgSpriteIcon
                                            name='CustomDeveloper'
                                            width={40}
                                            height={40}
                                        />
                                    )
                                }
                            </div>
                        </a>
                    )}
                    {renderButtons()}
                </div>
            </div>
            {
                hasFavoritesButton ? (
                    <FavoritesButton
                        {...favoritesButtonProps}
                        className={cn('favorites')}
                    />
                ) : null
            }
        </div>
    );
};

export default RightBlock;
